import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/category/movies', label: t('nav.movies') },
    { path: '/category/tv', label: t('nav.tv') },
    { path: '/category/variety', label: t('nav.variety') },
    { path: '/category/anime', label: t('nav.anime') },
    { path: '/about', label: t('nav.about') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <nav className="flex items-center justify-between px-4 md:px-6 h-16 max-w-[1400px] mx-auto">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="text-2xl font-heading font-bold text-primary-500">
            蜜桃影视
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${
                isActive(link.path)
                  ? 'bg-primary-500 text-background-50'
                  : scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder={t('nav.search')}
              className={`pl-10 pr-4 py-2 rounded-full text-sm w-48 focus:w-64 transition-all duration-300 outline-none ${
                scrolled
                  ? 'bg-background-100 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-primary-400'
                  : 'bg-white/15 text-white placeholder:text-white/60 border border-white/20 focus:bg-white/25'
              }`}
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none"></i>
          </div>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="菜单"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl ${scrolled ? 'text-foreground-900' : 'text-white'}`}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="flex flex-col px-4 py-3 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  isActive(link.path)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}